import { useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

export default function VisaGuideAmericansIreland() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openRelocationExpert = () => {
    const expertButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (expertButton) {
      expertButton.click();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      <article className="pt-24 pb-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#0A381F] to-emerald-800 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 text-emerald-200 mb-4">
              <a href="/blog" className="hover:text-white transition-colors">Blog</a>
              <i className="ri-arrow-right-s-line"></i>
              <span>Visa Guide</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Ireland Visa Guide for Americans: Complete 2024 Work Permit & Immigration Guide
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-emerald-100">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                January 15, 2024
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                20 min read
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-user-line"></i>
                Immigration Expert
              </span>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
            <img
              src="https://public.readdy.ai/ai/img_res/064ba5f81f263600aab7acd6906f7f19.jpg"
              alt="American passport with Ireland visa and work permit documents"
              className="w-full h-96 object-cover object-top"
            />
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Moving to Ireland from the United States requires navigating Irish immigration law and obtaining the correct visa or work permit. For Americans planning to work in Ireland, understanding the visa process is crucial for a successful relocation. This comprehensive guide covers every visa option available to Americans, from Critical Skills Employment Permits to student visas, with step-by-step application instructions and insider tips from immigration experts.
            </p>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-2xl text-blue-600 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-blue-900 mb-2">Good News for Americans</h3>
                  <p className="text-blue-800 mb-0">
                    Americans can visit Ireland visa-free for up to 90 days for tourism. However, to work or stay longer, you'll need a work permit or other authorization. The good news: Ireland actively welcomes skilled American workers, and the process is straightforward for qualified professionals.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="bg-gray-50 rounded-xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-list-check text-[#0A381F]"></i>
              Complete Visa Guide Contents
            </h2>
            <ul className="grid md:grid-cols-2 gap-3">
              {[
                'Visa-Free Entry for Americans (90 Days)',
                'Critical Skills Employment Permit',
                'General Employment Permit',
                'Intra-Company Transfer Permit',
                'Stamp 1G Graduate Visa',
                'Student Visa (Stamp 2)',
                'Startup Entrepreneur Programme',
                'Irish Ancestry Visa Options',
                'Application Process Step-by-Step',
                'Processing Times and Costs',
                'Common Rejection Reasons',
                'Path to Permanent Residency',
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-[#0A381F] flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Main Content Sections */}
          
          {/* Section 1: Visa-Free Entry */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#0A381F] text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">1</span>
              Visa-Free Entry for Americans: What You Can (and Can't) Do
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              As an American citizen, you can enter Ireland without a visa for tourism, business meetings, or short visits up to 90 days within a 180-day period. This is perfect for Americans who want to scout locations, attend job interviews, or explore Ireland before committing to relocation. However, there are strict limitations on what you can do during visa-free stays.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-check-double-line text-green-600"></i>
                  What Americans CAN Do (Visa-Free)
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Tourism and sightseeing throughout Ireland</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Attend business meetings and conferences</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Job interviews with Irish employers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">House hunting and apartment viewings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Short-term training or workshops (unpaid)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Visit family and friends</span>
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-close-circle-line text-red-600"></i>
                  What Americans CANNOT Do (Visa-Free)
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Work for any Irish employer (even remotely)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Receive payment from Irish companies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Enroll in long-term education programs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Stay longer than 90 days in 180-day period</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Access public healthcare or benefits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Establish permanent residence</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-amber-900 mb-3">Important for Digital Nomad Americans</h3>
              <p className="text-amber-800 mb-3">
                Many Americans ask: "Can I work remotely for my US employer while visiting Ireland?" The answer is complicated:
              </p>
              <ul className="space-y-2 text-amber-800">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span><strong>Technically:</strong> Irish immigration law doesn't explicitly address remote work for foreign employers during tourist stays</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span><strong>Practically:</strong> Short-term remote work (checking emails, attending Zoom calls) during a 2-week vacation is generally tolerated</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span><strong>Risky:</strong> Spending 90 days working full-time remotely could be considered unauthorized employment</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span><strong>Recommendation:</strong> Americans planning extended remote work should apply for proper work authorization</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 2: Critical Skills Employment Permit */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#0A381F] text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">2</span>
              Critical Skills Employment Permit: Best Option for Most Americans
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              The Critical Skills Employment Permit is the most popular and advantageous work permit for Americans moving to Ireland. It's designed for highly skilled professionals in sectors where Ireland has labor shortages. This permit offers the fastest path to permanent residency and allows Americans to bring family members to Ireland immediately.
            </p>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Critical Skills Permit Benefits for Americans</h3>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <i className="ri-time-line text-2xl text-blue-600"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 text-center mb-2">Fast Processing</h4>
                  <p className="text-gray-700 text-sm text-center">
                    8-12 weeks typical processing time for Americans
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <i className="ri-home-heart-line text-2xl text-green-600"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 text-center mb-2">Family Included</h4>
                  <p className="text-gray-700 text-sm text-center">
                    Spouse and children can join you immediately
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <i className="ri-shield-check-line text-2xl text-purple-600"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 text-center mb-2">Path to PR</h4>
                  <p className="text-gray-700 text-sm text-center">
                    Eligible for permanent residency after just 2 years
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Eligibility Requirements for Americans</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#0A381F] text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Job Offer from Irish Employer</h4>
                    <p className="text-gray-700 mb-2">
                      You must have a firm job offer from an Irish company before applying. The employer must be registered in Ireland and willing to sponsor your permit.
                    </p>
                    <div className="bg-gray-50 rounded p-3">
                      <p className="text-sm text-gray-600">
                        <strong>Tip for Americans:</strong> Many US tech companies with Irish offices (Google, Meta, Apple) can facilitate internal transfers for American employees.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#0A381F] text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Minimum Salary Requirement</h4>
                    <p className="text-gray-700 mb-2">
                      Your annual salary must be at least <strong>€30,000</strong> (approximately $32,700 USD). For most Americans in skilled roles, this threshold is easily met.
                    </p>
                    <div className="bg-emerald-50 rounded p-3">
                      <p className="text-sm text-emerald-800">
                        <strong>Average salaries for Americans:</strong> Software Engineers €60k-120k, Financial Analysts €50k-90k, Marketing Managers €45k-80k
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#0A381F] text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Eligible Occupation</h4>
                    <p className="text-gray-700 mb-2">
                      Your job must be on Ireland's Critical Skills Occupations List. Good news for Americans: most tech, engineering, healthcare, and finance roles qualify.
                    </p>
                    <div className="bg-blue-50 rounded p-3">
                      <p className="text-sm text-blue-800 mb-2">
                        <strong>Top eligible roles for Americans:</strong>
                      </p>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Software Developers & Engineers</li>
                        <li>• Data Scientists & Analysts</li>
                        <li>• ICT Professionals</li>
                        <li>• Engineers (all disciplines)</li>
                        <li>• Healthcare Professionals</li>
                        <li>• Financial Services Professionals</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#0A381F] text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Qualifications and Experience</h4>
                    <p className="text-gray-700 mb-2">
                      You must have either:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-graduation-cap-line text-[#0A381F] flex-shrink-0 mt-1"></i>
                        <span>A degree or higher qualification relevant to the role, OR</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-briefcase-line text-[#0A381F] flex-shrink-0 mt-1"></i>
                        <span>Significant relevant work experience (typically 5+ years for Americans without degrees)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-green-900 mb-3">Why Americans Love the Critical Skills Permit</h3>
              <ul className="space-y-2 text-green-800">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                  <span><strong>No labor market test:</strong> Your employer doesn't need to prove they couldn't find an Irish/EU worker</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                  <span><strong>Immediate family reunification:</strong> Bring your spouse and kids right away (they can work/study too)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                  <span><strong>Job flexibility:</strong> After 12 months, you can change employers without reapplying</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                  <span><strong>Fast track to PR:</strong> Apply for permanent residency after just 2 years (vs 5 years for other permits)</span>
                </li>
              </ul>
            </div>
          </section>

          {/* CTA Section */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Need Help with Your Irish Visa Application?</h2>
              <p className="text-lg text-gray-700 mb-6">
                Our Ireland Relocation Expert can guide you through the visa process, help you understand which permit is right for you, and connect you with immigration lawyers who specialize in helping Americans relocate to Ireland.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openRelocationExpert}
                  className="bg-[#0A381F] text-white px-8 py-4 rounded-xl font-bold hover:bg-emerald-800 transition-all hover-lift flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-question-answer-line text-xl"></i>
                  Ask About Visas
                </button>
                <a
                  href="/directory?category=Immigration"
                  className="bg-white text-[#0A381F] px-8 py-4 rounded-xl font-bold border-2 border-[#0A381F] hover:bg-[#0A381F]/5 transition-all hover-lift flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-search-line text-xl"></i>
                  Find Immigration Lawyers
                </a>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles for Americans</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <a href="/blog/articles/jobs-ireland-americans-2024" className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all hover-lift group">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-briefcase-line text-2xl text-emerald-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0A381F] transition-colors">
                  Jobs in Ireland for Americans
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Complete guide to finding work in Ireland as an American professional
                </p>
                <span className="text-[#0A381F] font-semibold text-sm flex items-center gap-1">
                  Read More <i className="ri-arrow-right-line"></i>
                </span>
              </a>

              <a href="/blog/articles/irish-tax-system-americans" className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all hover-lift group">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-money-dollar-circle-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0A381F] transition-colors">
                  Irish Tax System for Americans
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Everything Americans need to know about taxes in Ireland
                </p>
                <span className="text-[#0A381F] font-semibold text-sm flex items-center gap-1">
                  Read More <i className="ri-arrow-right-line"></i>
                </span>
              </a>

              <a href="/blog/articles/cost-of-living-usa-ireland" className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all hover-lift group">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-home-heart-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0A381F] transition-colors">
                  Cost of Living: USA vs Ireland
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Detailed cost comparison for Americans moving to Ireland
                </p>
                <span className="text-[#0A381F] font-semibold text-sm flex items-center gap-1">
                  Read More <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            </div>
          </section>
        </div>
      </article>

      <Footer />
    </div>
  );
}